import { apiFetch, unwrapData } from '@/utils/api';

/**
 * Carga completa de una ruta paginada por `limit`/`offset`, encadenando páginas.
 *
 * `parsePagination` del backend recorta cualquier `limit` mayor que `MAX_LIMIT` con un
 * `Math.min` mudo: ni cabecera, ni campo en la respuesta, ni error. Quien pedía la lista
 * "entera" en una sola llamada se llevaba una página y la trataba como el conjunto
 * completo, sin señal de que faltara nada:
 *
 *   - el listado de facturas pedía 1000, recibía 200 de 436 y filtraba, buscaba, contaba
 *     las pestañas y paginaba sobre esas 200. Como ordena por fecha descendente, lo que
 *     se caía por el borde era lo más antiguo: 3 facturas VENCIDAS que no salían en la
 *     pestaña «Vencidas» (marcaba 0), ni en el buscador, ni en el export.
 *   - el export a XLSX, que se ofrece como «todas las facturas», exportaba 200 de 436
 *     y el toast cantaba «200 facturas exportadas» tan tranquilo.
 *
 * Es el mismo defecto que `lib/bankMovementsPaging.js` arregló para movimientos
 * bancarios, y de ahí sale este contrato.
 *
 * Devuelve `{ items, total, truncated }`. `truncated` significa que el servidor tiene
 * más filas de las devueltas: quien pide el conjunto entero DEBE avisarlo — cortar en
 * silencio es justo el defecto que esto arregla. Quien pagina de verdad (páginas de 10,
 * 25…) lo ignora, porque ahí es lo normal.
 */

/** Tope REAL por petición del backend (`MAX_LIMIT` en verix-api/src/utils/pagination.ts). */
export const SERVER_PAGE_LIMIT = 200;

/** Techo del acumulado (25 páginas), para no encadenar peticiones sin fin. */
export const PAGING_HARD_CAP = 5000;

/**
 * `paging: 'page'` es para rutas que paginan con `page` (base 0) y `page_size` y
 * devuelven el total en `pagination.total` (p. ej. `/contact-directory`, cuyo schema
 * `.strict()` rechaza `limit`/`offset`). En ese modo cada petición pide páginas
 * completas de `SERVER_PAGE_LIMIT` y `offset` no se admite.
 *
 * `paging: 'l10'` es el contrato L-10 de la épica de listas: `page` base 1, `page_size`
 * y `total` en la raíz (`{ data, total, page, pageSize }`). Si la respuesta no trae
 * `total` es el backend anterior a la migración, que ignora la paginación y devuelve
 * todas las filas de golpe: se para ahí, porque pedir la página 2 traería las mismas.
 */
export async function fetchAllPages(route, opts = {}) {
  const { filters = {}, pageSize = PAGING_HARD_CAP, offset = 0, hardCap = PAGING_HARD_CAP, paging = 'offset' } = opts;
  const porPagina = paging === 'page' || paging === 'l10';

  const perRequest = Math.min(pageSize, SERVER_PAGE_LIMIT);
  const wanted = Math.min(pageSize, hardCap);
  const items = [];
  let total = null;

  for (let fetched = 0; fetched < wanted; fetched += perRequest) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
    }
    if (porPagina) {
      params.set('page', String(fetched / perRequest + (paging === 'l10' ? 1 : 0)));
      params.set('page_size', String(perRequest));
    } else {
      params.set('limit', String(Math.min(perRequest, wanted - fetched)));
      params.set('offset', String(offset + fetched));
    }

    const result = await apiFetch(`${route}?${params.toString()}`);
    const page = unwrapData(result).slice(0, wanted - fetched);
    items.push(...page);
    const totalRespuesta = paging === 'page' ? result?.pagination?.total : result?.total;
    if (typeof totalRespuesta === 'number') total = totalRespuesta;
    if (paging === 'l10' && total === null) break;

    // Página incompleta = no hay más. Es la única señal fiable: `total` puede faltar
    // (las rutas crudFactory lo omiten cuando se piden con `count=false`).
    if (page.length < perRequest) break;
    if (total !== null && offset + items.length >= total) break;
  }

  const knownTotal = total ?? items.length;
  return { items, total: knownTotal, truncated: knownTotal > offset + items.length };
}
